
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ImageUpload } from "@/components/ImageUpload";
import { useProviderGallery, useAddGalleryImage, useUpdateGalleryImage, useDeleteGalleryImage, ProviderGalleryImage } from "@/hooks/useProviderGallery";
import { Images, Plus, Edit, Trash2, GripVertical } from "lucide-react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";

interface GalleryManagementProps {
  providerId: string;
}

const GalleryManagement = ({ providerId }: GalleryManagementProps) => {
  const { data: galleryImages = [], isLoading } = useProviderGallery(providerId);
  const addGalleryImage = useAddGalleryImage();
  const updateGalleryImage = useUpdateGalleryImage();
  const deleteGalleryImage = useDeleteGalleryImage();

  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [editingImage, setEditingImage] = useState<ProviderGalleryImage | null>(null);
  const [newImageUrl, setNewImageUrl] = useState("");
  const [newImageAltText, setNewImageAltText] = useState("");

  const handleAddImage = async () => {
    if (!newImageUrl) return;

    const maxOrder = Math.max(...galleryImages.map(img => img.display_order), -1);
    
    await addGalleryImage.mutateAsync({
      provider_id: providerId,
      image_url: newImageUrl,
      alt_text: newImageAltText || undefined,
      display_order: maxOrder + 1,
    });

    setNewImageUrl("");
    setNewImageAltText("");
    setIsAddDialogOpen(false);
  };

  const handleUpdateImage = async () => {
    if (!editingImage) return;

    await updateGalleryImage.mutateAsync({
      id: editingImage.id,
      alt_text: editingImage.alt_text,
    });

    setEditingImage(null);
  };

  const handleDeleteImage = async (imageId: string) => {
    await deleteGalleryImage.mutateAsync({
      id: imageId,
      provider_id: providerId,
    });
  };

  const handleDragEnd = async (result: any) => {
    if (!result.destination) return;

    const items = Array.from(galleryImages);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Update display orders
    for (let i = 0; i < items.length; i++) {
      if (items[i].display_order !== i) {
        await updateGalleryImage.mutateAsync({
          id: items[i].id,
          display_order: i,
        });
      }
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <p className="text-gray-600">Loading gallery...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          <CardTitle className="flex items-center space-x-2">
            <Images className="h-5 w-5" />
            <span>Gallery Management</span>
          </CardTitle>
          
          <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Add Image
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Add Gallery Image</DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4">
                <div>
                  <Label>Image</Label>
                  <ImageUpload
                    onImageUploaded={setNewImageUrl}
                    currentImageUrl={newImageUrl}
                    bucketName="providers"
                    folder="gallery"
                  />
                </div>
                
                <div>
                  <Label htmlFor="alt-text">Alt Text (Optional)</Label>
                  <Input
                    id="alt-text"
                    value={newImageAltText}
                    onChange={(e) => setNewImageAltText(e.target.value)}
                    placeholder="Description of the image"
                  />
                </div>
                
                <div className="flex justify-end space-x-2">
                  <Button
                    variant="outline"
                    onClick={() => setIsAddDialogOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleAddImage}
                    disabled={!newImageUrl || addGalleryImage.isPending}
                  >
                    {addGalleryImage.isPending ? "Adding..." : "Add Image"}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>

      <CardContent>
        {galleryImages.length === 0 ? (
          <div className="text-center py-8">
            <Images className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <p className="text-gray-600 mb-4">No gallery images yet.</p>
            <p className="text-sm text-gray-500">Add images to showcase your facilities and services.</p>
          </div>
        ) : (
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="gallery">
              {(provided) => (
                <div
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
                >
                  {galleryImages.map((image, index) => (
                    <Draggable key={image.id} draggableId={image.id} index={index}>
                      {(provided) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          className="relative group bg-white border rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                        >
                          <div {...provided.dragHandleProps} className="absolute top-2 left-2 z-10 opacity-0 group-hover:opacity-100 transition-opacity">
                            <GripVertical className="h-4 w-4 text-white bg-black bg-opacity-50 rounded p-0.5" />
                          </div>
                          
                          <img
                            src={image.image_url}
                            alt={image.alt_text || `Gallery image ${index + 1}`}
                            className="w-full h-48 object-cover"
                          />
                          
                          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200">
                            <div className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity space-x-1">
                              <Button
                                size="sm"
                                variant="secondary"
                                onClick={() => setEditingImage(image)}
                              >
                                <Edit className="h-3 w-3" />
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => handleDeleteImage(image.id)}
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>
                          
                          {image.alt_text && (
                            <div className="p-2">
                              <p className="text-xs text-gray-600 truncate">{image.alt_text}</p>
                            </div>
                          )}
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        )}

        {/* Edit Dialog */}
        <Dialog open={!!editingImage} onOpenChange={(open) => !open && setEditingImage(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Edit Gallery Image</DialogTitle>
            </DialogHeader>
            
            {editingImage && (
              <div className="space-y-4">
                <div>
                  <img
                    src={editingImage.image_url}
                    alt={editingImage.alt_text || "Gallery image"}
                    className="w-full h-32 object-cover rounded border"
                  />
                </div>
                
                <div>
                  <Label htmlFor="edit-alt-text">Alt Text</Label>
                  <Input
                    id="edit-alt-text"
                    value={editingImage.alt_text || ""}
                    onChange={(e) => setEditingImage({
                      ...editingImage,
                      alt_text: e.target.value
                    })}
                    placeholder="Description of the image"
                  />
                </div>
                
                <div className="flex justify-end space-x-2">
                  <Button
                    variant="outline"
                    onClick={() => setEditingImage(null)}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleUpdateImage}
                    disabled={updateGalleryImage.isPending}
                  >
                    {updateGalleryImage.isPending ? "Updating..." : "Update"}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default GalleryManagement;
